import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { ImageReveal } from '../components/ImageReveal';
import { MetricsSection } from '../components/MetricsSection';
import { CTASection } from '../components/CTASection';
import { images } from '../data/site';
const pillars = [{
  title: 'Venture building',
  body: 'Companies built from thesis to first revenue, with founding teams who own what they build.'
}, {
  title: 'Investing',
  body: 'Early cheques and long patience, concentrated in fintech, climate, health and applied AI.'
}, {
  title: 'Ecosystems',
  body: 'The connective tissue between policy, institutions and founders — designed to outlive its funding.'
}, {
  title: 'Leadership',
  body: 'Coaching founders through the step change from proving a product to running a company.'
}];
const timeline = [{
  year: '2013',
  text: 'Started my first company. Learned what capital scarcity actually teaches.'
}, {
  year: '2016',
  text: 'Led operations through a scale-up from 12 to 140 people across three markets.'
}, {
  year: '2019',
  text: 'Crossed the table. Began investing at pre-seed and advising first-time managers.'
}, {
  year: '2021',
  text: 'Founded a venture studio building companies in frontier markets.'
}, {
  year: '2023',
  text: 'Designed a national ecosystem strategy adopted across three West African markets.'
}, {
  year: '2026',
  text: 'Building, backing and convening — across 30 countries and counting.'
}];
export function About() {
  return <>
      <PageHeader eyebrow="About" title="Building what matters." intro="I have spent my career on both sides of the table — building companies from nothing, and backing the people who do." data-id="element-167" data-mp="2m8pww53ii9wa" />

      <section className="px-6 sm:px-10" data-id="element-168" data-mp="2w6cfp2nxmllj">
        <div className="mx-auto grid max-w-[92rem] gap-14 lg:grid-cols-12 lg:gap-20" data-id="element-169" data-mp="2w7f6zbp62q1z">
          <div className="lg:col-span-5" data-id="element-170" data-mp="2r9m00gg1hs0n">
            <ImageReveal src={images.aboutPortrait} alt="Portrait of Amoye Henry" className="aspect-[4/5] rounded-[2rem] border border-charcoal/[0.07]" parallax={24} data-id="element-171" data-mp="1ouwifq2wq7vi" />
            <Reveal delay={0.1} data-id="element-172" data-mp="1xu9r4gj7isf6">
              <p className="mt-8 font-script text-3xl text-mocha" data-id="element-173" data-mp="1xt85ji4kenj7">Amoye</p>
            </Reveal>
          </div>

          <div className="space-y-8 text-[1.0625rem] leading-[1.85] text-charcoal/70 lg:col-span-6 lg:col-start-7 lg:pt-6" data-id="element-174" data-mp="22nvz3vefe84n">
            <Reveal data-id="element-175" data-mp="2cjpi9xmngzve">
              <p className="font-serif text-[1.75rem] font-light leading-[1.35] text-charcoal" data-id="element-176" data-mp="2cj614j4tqfif">
                Entrepreneurship taught me the cost of building without
                infrastructure. Investing taught me how rarely capital solves
                that alone.
              </p>
            </Reveal>
            <Reveal delay={0.08} data-id="element-177" data-mp="m92z22b4mr88">
              <p data-id="element-178" data-mp="ma5qcbcd2voo">
                I started as a founder, and the years that followed took me
                through operating roles, investment committees, government rooms
                and boardrooms. What connects them is a single question: what has
                to exist for ambitious people to build here?
              </p>
            </Reveal>
            <Reveal delay={0.14} data-id="element-179" data-mp="9ybqnqk5r20u">
              <p data-id="element-180" data-mp="9vh8k5om9tal">
                Today I build companies through a venture studio, invest at the
                earliest stages, advise institutions deploying capital into
                emerging markets, and design the ecosystems that make both
                possible. I sit on boards. I mentor founders. I convene the
                people who otherwise never meet.
              </p>
            </Reveal>
            <Reveal delay={0.2} data-id="element-181" data-mp="spw3nk7t51t5">
              <p data-id="element-182" data-mp="on7ufbf6lsvn">
                The work is global, but the centre of gravity is Africa — the
                market where the next generation of category-defining companies
                will be built, and where the infrastructure to build them is
                still being written.
              </p>
            </Reveal>
            <Reveal delay={0.26} data-id="element-183" data-mp="112edzlbn8lip">
              <p className="text-charcoal" data-id="element-184" data-mp="11261vvnchk8k">
                I am interested in what lasts: companies that outlive their
                funding, markets that outlive their moment, and founders who
                outlive their first idea.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-6 py-28 sm:px-10 sm:py-40" data-id="element-185" data-mp="1jvi97b4if6ff">
        <div className="mx-auto max-w-[92rem]" data-id="element-186" data-mp="1otjr3sq78zpb">
          <Reveal data-id="element-187" data-mp="1os8p9ygbe20u">
            <p className="eyebrow text-mocha" data-id="element-188" data-mp="1oss77f5ysqev">How the work divides</p>
          </Reveal>
          <div className="mt-14 grid gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-4" data-id="element-189" data-mp="1at8z2md5lf2k">
            {pillars.map((p, i) => <Reveal key={p.title} delay={i * 0.08} data-id="element-190" data-mp="1ex8dmzevi3ta" data-mpi={i}>
                <div className="border-t border-charcoal/15 pt-6" data-id="element-191" data-mp="3viyb6rmh2mrk">
                  <h3 className="font-serif text-[1.625rem] font-light leading-tight text-charcoal" data-id="element-192" data-mp="3fyxalr1jrw7">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/60" data-id="element-193" data-mp="3s09045a9zxq8">
                    {p.body}
                  </p>
                </div>
              </Reveal>)}
          </div>
        </div>
      </section>

      <MetricsSection data-id="element-194" data-mp="vsrs9ms3tdj" />

      {/* Timeline */}
      <section className="px-6 py-28 sm:px-10 sm:py-40" data-id="element-195" data-mp="x3wcbo1t55p">
        <div className="mx-auto grid max-w-[92rem] gap-14 lg:grid-cols-12" data-id="element-196" data-mp="39bchwzs5kvod">
          <div className="lg:col-span-4" data-id="element-197" data-mp="399hrj5y44bcw">
            <Reveal data-id="element-198" data-mp="34dan5hld77x2">
              <p className="eyebrow text-mocha" data-id="element-199" data-mp="34du3itvd9o8z">The path</p>
              <h2 className="mt-6 font-serif text-[2.5rem] font-light leading-[1.04] text-charcoal sm:text-[3.25rem]" data-id="element-200" data-mp="3j15iz6uo5sed">
                A career built in both directions.
              </h2>
            </Reveal>
          </div>
          <ol className="lg:col-span-7 lg:col-start-6" data-id="element-201" data-mp="2qk2vjpxaacfd">
            {timeline.map((t, i) => <Reveal key={t.year} delay={i * 0.06} as="li" data-id="element-202" data-mp="2lkymr0e9ugpk" data-mpi={i}>
                <div className="grid grid-cols-[4.5rem_1fr] gap-6 border-t border-charcoal/[0.1] py-7" data-id="element-203" data-mp="3ba5fa7wzktnp">
                  <span className="font-serif text-xl text-mocha" data-id="element-204" data-mp="3fbqva24tho24">{t.year}</span>
                  <p className="text-[1.0625rem] leading-relaxed text-charcoal/70" data-id="element-205" data-mp="3f9e7h8cdojh5">
                    {t.text}
                  </p>
                </div>
              </Reveal>)}
          </ol>
        </div>
      </section>

      <CTASection data-id="element-206" data-mp="2sbt1u9z1kxmb" />
    </>;
}